import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiChevronDown, FiChevronUp } from 'react-icons/fi'

import { Alert } from '@vtex/shoreline'
import moment from 'moment'

import BadgeIsValid from '~/components/BadgeIsValid'
import Panel from '~/components/Panel'
import {
  Checkbox,
  FormGroup,
  Input,
  InputMask,
  Label,
  Textarea
} from '~/components/unform'
import { getDurationInDays, formatHour } from '~/helpers'

import * as S from './styles'

/**
 * Este componente retornará um Panel conteúdo os campos necessários para cadastro de
 * nome, descrição(opcional) e um intervalo de tempo com data e horário de início e fim.
 */

const NameAndTimeInterval = ({
  formRef,
  titlePanel,
  labelTitle,
  placeholderTitle,
  labelDescription,
  placeholderDescription,
  hasDescription,
  labelInterval,
  onlyDates,
  endDateIsRequired = true,
  isEditMode,
  showValidBadgeOnSection = false,
  intervalAlertMessage = null,
  showEndDateOptional = false,
  containerElement = null,
  startDateReadOnly = false,
  autoFocus = 'name',
  initialData,
  onChangeDate,
  canEditDate = true,
  initialCollapsibleState,
  onSectionValidityChange
}: NameAndTimeIntervalProps) => {
  const { t } = useTranslation()

  const [name, setName] = useState('')
  const [endDate, setEndDate] = useState('')
  const [startDate, setStartDate] = useState('')
  const [hasEndDate, setHasEndDate] = useState(
    !showEndDateOptional || !!initialData?.end_date
  )

  // Native `autoFocus` only applies on mount. The offsite flow mounts
  // with autoFocus={false} and later flips to 'name' once a channel is
  // picked — without this, that update would open the panel but leave
  // the name field unfocused.
  const previousAutoFocusRef = useRef(autoFocus)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const previousAutoFocus = previousAutoFocusRef.current
    previousAutoFocusRef.current = autoFocus

    if (previousAutoFocus === false && autoFocus !== false) {
      rootRef.current
        ?.querySelector<HTMLInputElement>(`input[name="${autoFocus}"]`)
        ?.focus()
    }
  }, [autoFocus])

  const handleChangeDate = useCallback(() => {
    const isAlwaysOn = formRef?.current?.getFieldValue('hasFinalDate')

    const startDate = formRef?.current?.getFieldValue('start_date')
    const endDateRaw = formRef?.current?.getFieldValue('end_date')
    const endDate = isAlwaysOn ? null : endDateRaw

    const duration = getDurationInDays({ startDate, endDate })

    setEndDate(endDate)
    setStartDate(startDate)

    formRef?.current?.setFieldValue('duration', duration)

    onChangeDate?.({
      startDate,
      endDate,
      isAlwaysOn,
      duration: getDurationInDays({
        startDate,
        endDate,
        returnType: 'number'
      }) as number
    })
  }, [formRef, onChangeDate])

  // `showEndDateOptional` was always static for every caller until the
  // offsite channel flow started passing a value that flips mid-lifecycle
  // (as soon as a channel — and the briefing permission — apply). The
  // `useState` initializer above only runs on mount, so without this
  // effect, turning the option off after mount removed the "no end date"
  // checkbox but left `hasEndDate` at whatever it already was — if that
  // was `false`, the end date field became permanently unreachable, with
  // no checkbox left to bring it back.
  //
  // wasEndDateOptionalRef guards the recompute below to an actual
  // true→false transition, never the initial mount: every other caller of
  // this shared component always mounts with showEndDateOptional at its
  // default (false), and recomputing on every one of their mounts (instead
  // of only when `initialStartDate` is present, as the effect further below
  // already does) would be a behavior change for all of them, not just this
  // flow.
  const wasEndDateOptionalRef = useRef(showEndDateOptional)
  const pendingRecomputeRef = useRef(false)

  useEffect(() => {
    if (!showEndDateOptional) {
      setHasEndDate(true)

      if (wasEndDateOptionalRef.current) {
        pendingRecomputeRef.current = true
      }
    }

    wasEndDateOptionalRef.current = showEndDateOptional
  }, [showEndDateOptional])

  // Runs once `hasEndDate` actually commits as true (the end-date field is
  // in the DOM by then), not in the effect above: the "no end date"
  // checkbox (Unform field `hasFinalDate`) may still read as checked from
  // before the flip, and handleChangeDate reads it as "always on", forcing
  // `endDate` to null — recomputing here (now that the checkbox is gone)
  // refreshes `endDate`/`onChangeDate` immediately instead of leaving them
  // stuck at null until the operator edits a date field. Gated behind the
  // pending flag so it never fires for the operator's own manual toggles of
  // the "no end date" checkbox, only for this specific transition.
  useEffect(() => {
    if (pendingRecomputeRef.current) {
      pendingRecomputeRef.current = false
      handleChangeDate()
    }
  }, [hasEndDate, handleChangeDate])

  useEffect(() => {
    const initialName = formRef?.current?.getFieldValue('name')
    const initialStartDate = formRef?.current?.getFieldValue('start_date')

    if (initialName) setName(initialName)
    if (initialStartDate) handleChangeDate()
  }, [formRef, handleChangeDate])

  const handleChange = useCallback(({ value }) => setName(value), [])

  const isValid = useMemo(() => {
    if (!showValidBadgeOnSection) {
      return false
    }

    const nameIsValid = name.length > 0
    const startDateIsValid = moment().isSameOrBefore(startDate, 'day')
    const endDateIsValid =
      !endDateIsRequired || moment(endDate).isSameOrAfter(startDate, 'day')

    return nameIsValid && startDateIsValid && endDateIsValid
  }, [endDate, endDateIsRequired, name, showValidBadgeOnSection, startDate])

  useEffect(() => {
    onSectionValidityChange?.(isValid)
  }, [isValid, onSectionValidityChange])

  const iconLeft = useMemo(() => <BadgeIsValid isValid={isValid} />, [isValid])

  const handleEndDate = useCallback(() => {
    setHasEndDate(state => !state)
  }, [])

  // Podemos sobrescrever o elemento usado no container
  // Caso não queira usar o Panel padrão, podemos usar DIV ou qualquer outro
  const ContainerElement = useMemo(
    () => containerElement || Panel,
    [containerElement]
  )

  const containerPanelProps = useMemo(() => {
    let className = ''
    if (showValidBadgeOnSection) {
      className = isValid ? '' : 'border-danger'
    }

    const panelProps = {
      title: titlePanel,
      isCollapsible: true,
      iconLeft: showValidBadgeOnSection ? iconLeft : null,
      className
    }

    if (typeof initialCollapsibleState === 'boolean') {
      return { ...panelProps, initialCollapsibleState }
    }

    return panelProps
  }, [
    iconLeft,
    initialCollapsibleState,
    isValid,
    showValidBadgeOnSection,
    titlePanel
  ])

  // Handle description
  const [showDescription, setShowDescription] = useState(false)

  return (
    <div ref={rootRef}>
      <ContainerElement {...(containerElement ? {} : containerPanelProps)}>
        <S.Group>
          {labelInterval && <S.StrongTitle>{labelInterval}</S.StrongTitle>}

          {canEditDate && (
            <S.DateWrapper>
              <div className="start">
                <FormGroup>
                  <Label htmlFor="start_date" isRequired>
                    {t('c:components.NameAndTimeInterval.startDate')}
                  </Label>
                  <Input
                    name="start_date"
                    autoFocus={autoFocus === 'start_date'}
                    type="date"
                    required
                    min={isEditMode ? null : moment().format('YYYY-MM-DD')}
                    max={endDate}
                    onChange={handleChangeDate}
                    disabled={startDateReadOnly}
                  />
                </FormGroup>

                {!onlyDates && (
                  <FormGroup>
                    <Label htmlFor="start_hour" isRequired>
                      {t('c:components.NameAndTimeInterval.startHour')}
                    </Label>
                    <InputMask
                      name="start_hour"
                      format={formatHour}
                      mask="_"
                      placeholder="HH:MM"
                      required
                      disabled={startDateReadOnly}
                    />
                  </FormGroup>
                )}
              </div>

              {hasEndDate && (
                <>
                  <div className="end">
                    <FormGroup>
                      <Label htmlFor="end_date" isRequired={endDateIsRequired}>
                        {t('c:components.NameAndTimeInterval.endDate')}
                      </Label>
                      <Input
                        name="end_date"
                        autoFocus={autoFocus === 'end_date'}
                        type="date"
                        required={endDateIsRequired}
                        min={
                          isEditMode ? moment().format('YYYY-MM-DD') : startDate
                        }
                        onChange={handleChangeDate}
                      />
                    </FormGroup>

                    {!onlyDates && (
                      <FormGroup>
                        <Label
                          htmlFor="end_hour"
                          isRequired={endDateIsRequired}
                        >
                          {t('c:components.NameAndTimeInterval.endHour')}
                        </Label>
                        <InputMask
                          name="end_hour"
                          format={formatHour}
                          mask="_"
                          placeholder="HH:MM"
                          required={endDateIsRequired}
                        />
                      </FormGroup>
                    )}
                  </div>

                  <div className="duration">
                    <FormGroup>
                      <Label>
                        {t('c:components.NameAndTimeInterval.duration')}
                      </Label>
                      <Input name="duration" disabled readOnly />
                    </FormGroup>
                  </div>
                </>
              )}

              {showEndDateOptional && (
                <S.WrapperCheckbox hasEndDate={hasEndDate}>
                  <Checkbox
                    name="hasFinalDate"
                    label={t('c:components.NameAndTimeInterval.noEndDate')}
                    onClick={handleEndDate}
                    isCheckedInitialValue={!initialData?.end_date && true}
                    className="checkbox"
                    onChange={handleChangeDate}
                  />
                </S.WrapperCheckbox>
              )}
            </S.DateWrapper>
          )}

          {!!intervalAlertMessage &&
            intervalAlertMessage.map((message, index) => (
              <div className="alertMessage" key={index}>
                <Alert variant="informational">{message}</Alert>
              </div>
            ))}
        </S.Group>

        <S.Group>
          <Label
            text={labelTitle}
            helperText={t('c:components.NameAndTimeInterval.name.helperText')}
            htmlFor="name"
            isRequired
          />
          <Input
            placeholder={placeholderTitle}
            name="name"
            id="name"
            required
            autoFocus={autoFocus === 'name'}
            onChange={handleChange}
          />
        </S.Group>

        {hasDescription && (
          <S.Group>
            <Label
              htmlFor="description"
              style={showDescription ? {} : { marginBottom: 0 }}
              onClick={() => setShowDescription(prevState => !prevState)}
              role="button"
            >
              {showDescription ? <FiChevronDown /> : <FiChevronUp />}&nbsp;
              {labelDescription}
            </Label>
            {showDescription && (
              <Textarea
                name="description"
                id="description"
                placeholder={placeholderDescription}
                minHeight="auto"
                rows="2"
              />
            )}
          </S.Group>
        )}
      </ContainerElement>
    </div>
  )
}

export default NameAndTimeInterval
